import { useEffect, useMemo, useState } from "react";
import { X, Sparkles, Zap, Wrench, Search, Check, AlertCircle } from "lucide-react";
import { CHANGELOG } from "../changelog";

const TAG_META = {
  novedad: {
    label: "Novedad",
    Icon: Sparkles,
    className:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  mejora: {
    label: "Mejora",
    Icon: Zap,
    className:
      "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  },
  arreglo: {
    label: "Arreglo",
    Icon: Wrench,
    className:
      "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  },
};

function formatDate(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}

// Minúsculas + sin acentos, para buscar sin que importen tildes/mayúsculas.
function normalize(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function WhatsNewModal({ open, onClose, newVersions }) {
  const [query, setQuery] = useState("");
  // Claves `${version}#${itemIndex}` marcadas como "Entendido" (en memoria, no persiste).
  const [acked, setAcked] = useState(() => new Set());
  // Pulso de atención del aviso cuando se intenta cerrar sin marcar todo.
  const [pulsing, setPulsing] = useState(false);

  // Conjunto de versiones que requieren acuse (Set). Defensivo ante prop ausente.
  const required = newVersions instanceof Set ? newVersions : new Set();

  // Todas las claves que el usuario debe confirmar, sobre el CHANGELOG completo
  // (no la lista filtrada): así el buscador no permite saltarse el acuse.
  const requiredKeys = useMemo(() => {
    const keys = [];
    CHANGELOG.forEach((entry) => {
      if (!required.has(entry.version)) return;
      (entry.items || []).forEach((_, i) => keys.push(`${entry.version}#${i}`));
    });
    return keys;
  }, [required]);

  const pendingCount = requiredKeys.reduce(
    (n, k) => (acked.has(k) ? n : n + 1),
    0,
  );
  const locked = pendingCount > 0;

  const toggle = (key) => {
    setAcked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Marca de una vez todas las novedades pendientes (no cierra el modal).
  const ackAll = () => setAcked(new Set(requiredKeys));

  // Llama la atención sobre el aviso (pulso breve) cuando se intenta cerrar bloqueado.
  const flagPending = () => {
    setPulsing(true);
    window.setTimeout(() => setPulsing(false), 600);
  };

  // Intento de cierre: si quedan novedades sin marcar, no cierra y avisa.
  const attemptClose = () => {
    if (locked) {
      flagPending();
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (locked) flagPending();
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, locked]);

  // Reiniciar buscador y marcas cada vez que se abre el modal.
  useEffect(() => {
    if (open) {
      setQuery("");
      setAcked(new Set());
      setPulsing(false);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return CHANGELOG;
    return CHANGELOG.filter((entry) => {
      const meta = TAG_META[entry.tag] || TAG_META.novedad;
      const haystack = normalize(
        [
          entry.title,
          entry.version,
          meta.label,
          formatDate(entry.date),
          ...(entry.items || []),
        ].join(" "),
      );
      return haystack.includes(q);
    });
  }, [query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 p-3 sm:p-4"
      onClick={attemptClose}
    >
      <div
        className="w-full max-w-2xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Novedades
          </h2>
          {/* No usamos `disabled`: un botón deshabilitado no recibe hover ni muestra
              el motivo. Lo dejamos clicable, pero al pulsarlo bloqueado solo avisa. */}
          <div className="relative">
            <button
              type="button"
              onClick={attemptClose}
              aria-disabled={locked}
              title={
                locked
                  ? "Marca las novedades como entendidas para cerrar"
                  : "Cerrar"
              }
              aria-label="Cerrar"
              className={`peer ${
                locked
                  ? "text-gray-300 dark:text-gray-700 cursor-not-allowed"
                  : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              }`}
            >
              <X size={20} />
            </button>
            {locked && (
              <span className="pointer-events-none absolute right-0 top-full z-10 mt-1.5 hidden whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg peer-hover:block dark:bg-gray-700">
                Marca las novedades como entendidas para cerrar
              </span>
            )}
          </div>
        </div>

        {locked && (
          <div className="shrink-0 px-5 sm:px-6 pt-3">
            <div
              className={`flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 ring-amber-400 transition-transform dark:bg-amber-900/20 dark:text-amber-300 ${
                pulsing ? "scale-[1.02] ring-2" : "ring-0"
              }`}
            >
              <AlertCircle size={14} className="shrink-0" />
              <span className="flex-1">
                {pendingCount === 1
                  ? "Te falta 1 novedad por marcar como entendida para poder cerrar."
                  : `Te faltan ${pendingCount} novedades por marcar como entendidas para poder cerrar.`}
              </span>
              <button
                type="button"
                onClick={ackAll}
                title="Marcar todas las novedades como entendidas"
                className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
              >
                <Check size={13} strokeWidth={3} /> Marcar todas
              </button>
            </div>
          </div>
        )}

        <div className="shrink-0 px-5 sm:px-6 pt-4">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar novedades…"
              aria-label="Buscar novedades"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 py-2 pl-9 pr-9 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Limpiar búsqueda"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-6">
          {filtered.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">
              No hay novedades que coincidan con «{query}».
            </p>
          )}
          {filtered.map((entry) => {
            const meta = TAG_META[entry.tag] || TAG_META.novedad;
            const Icon = meta.Icon;
            const needsAck = required.has(entry.version);
            return (
              <div key={entry.version}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.className}`}
                  >
                    <Icon size={13} /> {meta.label}
                  </span>
                  <span className="text-xs font-bold text-gray-400">
                    v{entry.version}
                  </span>
                  <span className="text-xs text-gray-400">
                    · {formatDate(entry.date)}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {entry.title}
                </h3>
                {needsAck ? (
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    {entry.items.map((it, i) => {
                      const key = `${entry.version}#${i}`;
                      const checked = acked.has(key);
                      return (
                        <li key={i}>
                          <label className="flex items-start gap-2 cursor-pointer select-none">
                            <span
                              className={`mt-0.5 shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${
                                checked
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : "border-gray-300 dark:border-gray-600 text-transparent"
                              }`}
                            >
                              <Check size={14} strokeWidth={3} />
                            </span>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={checked}
                              onChange={() => toggle(key)}
                            />
                            <span>{it}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                    {entry.items.map((it, i) => (
                      <li key={i}>{it}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
